import {
  BellAlertIcon,
  ClockIcon,
  Cog8ToothIcon,
  ComputerDesktopIcon,
  CubeIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  UsersIcon,
  ReceiptRefundIcon,
  DeviceTabletIcon,
  QrCodeIcon,
  DocumentIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  BoltIcon,
  MegaphoneIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { Revenue } from "./definitions";
import { DateTime } from "luxon";
import moment from "moment";
import momenttz from 'moment-timezone';
import Papa from 'papaparse';
import * as XLSX from "xlsx";
import { setSubscriptionData } from "./indexedDB";
import { ROLE_RANGES, VALID_PROVIDER_TYPES, PROVIDER_TYPES, INTEGRATIONS_TYPE, INTEGRATION_CREDENTIAL_FIELDS, AMAZON_OFFBOARDED_SOURCE, DEVICE_TYPE, type DeviceType, ATS_CHAT_FIREBASE_PREFIX, RTS_RETURN_REASONS } from "./constants";
import { getWeek, getWeekYear, startOfWeek, endOfWeek, subWeeks, format, subMonths, startOfMonth, endOfMonth, startOfDay, endOfDay, addDays, parseISO, lastDayOfMonth, subDays, parse, isBefore, formatDistanceToNow } from "date-fns";
import { toZonedTime, fromZonedTime, formatInTimeZone } from "date-fns-tz";
import { PDFPage, PDFFont, rgb, PDFDocument } from "pdf-lib";
import { z } from "zod";
import { plan } from "./schema";
import { followUpRuleSchema } from "@/app/validators/atsPipelineValidator";
import type { FollowUpRule } from "@/app/types/atsNotificationTypes";
import type { TimelineEvent } from "@/components/shared/Timeline";
import type {
  PackageMatchEntry,
  PackageTimelineSource,
} from "@/app/types/rtsTypes";

/**
 * Retrieves the device type from browser localStorage on the client side.
 *
 * The device type is set in localStorage under the key "deviceType" and can be
 * either 'personal' (the user's own phone/browser) or 'work' (a shared
 * handheld device used during a shift). This value determines which storage
 * path the backend uses for the FCM token:
 *   - 'personal' → stored in `users.notificationToken` (no expiration)
 *   - 'work'  → stored in `users.workDeviceNotificationToken` (expires at midnight)
 *
 * This function is safe to call during server-side rendering (SSR) — it
 * returns 'personal' when `window` is undefined (i.e., on the server).
 *
 * @returns DEVICE_TYPE.PERSONAL or DEVICE_TYPE.WORK
 */
export function getClientDeviceType(): DeviceType {
  // SSR guard: if we're on the server, there's no localStorage, so default to personal.
  if (typeof window === "undefined") {
    return DEVICE_TYPE.PERSONAL;
  }
  // Read the device type from localStorage. Only return 'work' if explicitly
  // set; anything else (including null/undefined) defaults to 'personal'.
  const stored = window.localStorage.getItem("deviceType");
  return stored === DEVICE_TYPE.WORK ? DEVICE_TYPE.WORK : DEVICE_TYPE.PERSONAL;
}

/**
 * Updates the document's `<link rel="manifest">` href to point at the
 * device-type-appropriate manifest file, so that if the user taps
 * "Add to Home Screen" the resulting installed icon's `start_url` (and
 * therefore its permanent launch URL) is baked in with the correct
 * `device` query param.
 *
 * This is purely a pre-install mechanism: it only matters while the user is
 * still viewing the page in a regular browser tab, before installing. Once a
 * PWA icon is actually created, its `start_url` is fixed forever and this
 * function has no further effect on that existing installation.
 *
 * Safe to call multiple times — the most recent call always wins, since it
 * simply overwrites the `href` attribute on the existing manifest link tag.
 * SSR-safe: no-ops when `document` is unavailable.
 *
 * @param deviceType - DEVICE_TYPE.WORK or DEVICE_TYPE.PERSONAL
 */
export function setManifestLink(deviceType: DeviceType): void {
  if (typeof document === "undefined") {
    return;
  }
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!manifestLink) {
    return;
  }
  manifestLink.href = deviceType === DEVICE_TYPE.WORK ? "/manifest-work.json" : "/manifest.json";
}

/**
 * Retrieves the client's local timezone (e.g. "America/New_York", "Europe/London")
 * using the browser's built-in Intl API.
 *
 * This timezone is sent to the backend during work device token registration
 * so the server can calculate when the token should expire (midnight in the
 * user's local timezone). Without this, the server would need to do extra DB
 * lookups to figure out the user's timezone.
 *
 * This function is SSR-safe — it returns `undefined` when running on the server
 * or if the Intl API is not available (very old browsers).
 *
 * @returns An IANA timezone string (e.g. "America/New_York") or undefined.
 */
export function getClientTimeZone(): string | undefined {
  // SSR guard: on the server, neither `window` nor `Intl.DateTimeFormat` may be available.
  if (typeof window === "undefined" || typeof Intl === "undefined" || !Intl.DateTimeFormat) {
    return undefined;
  }
  try {
    // The Intl API resolves the browser's timezone to an IANA string.
    // Example: "America/New_York", "Asia/Kolkata", "Europe/London"
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Some older browsers may throw if the timezone can't be resolved.
    return undefined;
  }
}

/**
 * Calculates the end of the current day (23:59:59.999) in the given timezone,
 * then converts that back to a UTC ISO 8601 string.
 *
 * This is used to set the `expiresAt` field on work device push notification
 * tokens. The idea is: a work device token should only be valid for the
 * current shift day. When midnight arrives in the user's local timezone, the
 * token is considered expired and will be automatically pruned on the next
 * read or write operation (see `pruneExpiredWorkTokens` in shared.ts).
 *
 * Example:
 *   - User is in "America/New_York" (UTC-5)
 *   - Current time: 2025-01-15 14:30 EST (19:30 UTC)
 *   - End of day in EST: 2025-01-15 23:59:59.999 EST
 *   - Converted to UTC: 2025-01-16 04:59:59.999 UTC
 *   - Returns: "2025-01-16T04:59:59.999Z"
 *
 * @param timeZone - An IANA timezone string (e.g. "America/New_York")
 * @returns UTC ISO 8601 string representing midnight in the given timezone
 */
export function calculateMidnightUtc(timeZone: string): string {
  // Step 1: Get the current UTC time.
  const nowUtc = new Date();

  // Step 2: Convert UTC time to the user's local timezone so we can work
  // with "local" hours/days without timezone offset confusion.
  const zonedNow = toZonedTime(nowUtc, timeZone);

  // Step 3: Set the time to the very end of the current day (23:59:59.999).
  // This uses date-fns `endOfDay` which handles all the edge cases.
  const zonedEndOfDay = endOfDay(zonedNow);

  // Step 4: Convert the zoned end-of-day BACK to UTC.
  // This gives us the exact UTC moment that corresponds to midnight local time.
  const utcEndOfDay = fromZonedTime(zonedEndOfDay, timeZone);

  // Step 5: Return as an ISO 8601 string (e.g. "2025-01-16T04:59:59.999Z").
  return utcEndOfDay.toISOString();
}

/**
 * Matches a path against a pattern (supports wildcards like /payroll/*)
 * Used by middleware for role checks and plan access checks
 * @param pattern The pattern to match against (e.g., "/payroll/*", "/settings")
 * @param path The path to check (e.g., "/payroll/compliance")
 * @returns Boolean indicating if the path matches the pattern
 */
export function matchPath(pattern: string, path: string): boolean {
  if (pattern.endsWith("/*")) {
    // Allow any route under the base path
    const basePath = pattern.slice(0, -2); // Remove `/*`
    return path.startsWith(basePath);
  }
  // Exact match
  return pattern === path;
}

/**
 * Determines if a role ID belongs to a provider role
 * @param roleId The role ID to check
 * @returns Boolean indicating if the role is a provider role
 */
export function isProviderRole(roleId: string | null | undefined): boolean {
  if (!roleId) return false;
  
  const roleIdNum = parseInt(roleId);
  return !isNaN(roleIdNum) && 
    roleIdNum >= ROLE_RANGES.PROVIDER.MIN && 
    roleIdNum <= ROLE_RANGES.PROVIDER.MAX;
}

/**
 * Determines if a provider type is valid
 * @param providerType The provider type to check
 * @returns Boolean indicating if the provider type is valid
 */
export function isProviderType(providerType: string | null | undefined): boolean {
  if (!providerType) return false;
  return VALID_PROVIDER_TYPES.includes(providerType);
}

/**
 * Determines if a user is a valid provider by checking both role and provider type
 * @param roleId The role ID to check
 * @param providerType The provider type to check
 * @returns Boolean indicating if the user is a valid provider
 */
export function isValidProvider(
  roleId: string | null | undefined,
  providerType: string | null | undefined
): boolean {
  return isProviderType(providerType) && isProviderRole(roleId);
}

/**
 * Determines if a user is a specific type of provider with proper range validation
 * First checks general provider validation, then validates the specific type and range
 * @param roleId The role ID to check
 * @param providerType The provider type to check
 * @param expectedProviderType The specific provider type to validate against
 * @returns Boolean indicating if the user is the expected provider type
 */
export function isSpecificProviderType(
  roleId: string | null | undefined,
  providerType: string | null | undefined,
  expectedProviderType: string
): boolean {
  // First check general provider validation
  if (!isValidProvider(roleId, providerType)) {
    return false;
  }

  // Then check if it matches the expected specific type
  if (providerType !== expectedProviderType) {
    return false;
  }

  // Check if roleId is provided
  if (!roleId) {
    return false;
  }

  // Finally check if the role is in the correct range for this provider type
  const roleIdNum = parseInt(roleId);
  if (isNaN(roleIdNum)) {
    return false;
  }

  const range = getProviderRoleRange(providerType, roleIdNum);
  return roleIdNum >= range.min && roleIdNum <= range.max;
}

/**
 * Get provider role range based on provider type and role ID
 * First checks if providerType matches roleId range
 * Falls back to general PROVIDER range if no specific match
 */
export function getProviderRoleRange(
  providerType: string | null | undefined,
  roleId: number
): { min: number; max: number } {
  if (providerType) {
    const providerTypeEntry = Object.entries(PROVIDER_TYPES).find(
      ([, value]) => value === providerType
    );

    if (providerTypeEntry) {
      const [typeKey] = providerTypeEntry;
      const rangeKey = `PROVIDER_${typeKey}` as keyof typeof ROLE_RANGES;
      const range = ROLE_RANGES[rangeKey];

      if (
        range &&
        roleId >= range.MIN &&
        roleId <= range.MAX
      ) {
        return { min: range.MIN, max: range.MAX };
      }
    }
  }

  // Fallback: Return general PROVIDER range
  return { min: ROLE_RANGES.PROVIDER.MIN, max: ROLE_RANGES.PROVIDER.MAX };
}


/**
 * Derive selected organization info (name, shortCode) from context options.
 */
export function getSelectedOrganizationInfo(
  selectedDspOrganizationId: string | number | null | undefined,
  serviceAreaOptions: Array<{
    organizationId?: string | number | null;
    organizationName?: string | null;
    shortCode?: string | null;
    value?: string;
  }> | null | undefined
): { name: string | null; shortCode: string | null } {
  try {
    if (!selectedDspOrganizationId || !serviceAreaOptions || serviceAreaOptions.length === 0) {
      return { name: null, shortCode: null };
    }

    const orgOption = serviceAreaOptions.find((opt: any) => 
      opt && opt.organizationId == selectedDspOrganizationId && opt.value !== 'All'
    );

    return {
      name: orgOption?.organizationName || null,
      shortCode: orgOption?.shortCode || null,
    };
  } catch (e) {
    return { name: null, shortCode: null };
  }
}

export const convertTo12HourFormat = (time24: string) => {
  const [hour, minute] = time24.split(":").map(Number);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12; // Converts 0 to 12 for AM
  return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
};

export const formatDateByTimezone = (date: any, timeZone: string, format: string) => {
  const formattedDate = momenttz
    .tz(
      moment(date).format(format),
      timeZone
    )
    .format(format);

  return formattedDate;
}

/**
 * Creates a pure UTC ISO string from a date and time without any timezone influence
 * This function treats the input date and time as pure UTC values
 * @param date - Date object (only date portion is used)
 * @param time - Time string in HH:mm format
 * @returns ISO string in UTC format (e.g., "2026-03-03T11:00:00Z")
 * @example
 * const date = new Date("2026-03-03");
 * const time = "11:00";
 * buildUtcIsoFromDateAndTime(date, time); // Returns: "2026-03-03T11:00:00Z"
 */
export const buildUtcIsoFromDateAndTime = (date: Date, time: string): string => {
  const [hh, mm] = time.split(":").map(Number);
  // Create pure UTC date without any timezone influence
  // Use local date components (not UTC) to preserve the user's selected date
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hh || 0,
    mm || 0,
    0,
    0
  ));
  
  const result = utcDate.toISOString();  
  return result;  // Returns: 2026-03-03T11:00:00.000Z
};

/**
 * Creates a pure UTC ISO string from a date at midnight (00:00:00) without any timezone influence
 * This function treats the input date as a pure UTC value
 * @param date - Date object (only date portion is used)
 * @returns ISO string in UTC format at midnight (e.g., "2026-03-03T00:00:00Z")
 * @example
 * const date = new Date("2026-03-03");
 * buildUtcIsoDateOnly(date); // Returns: "2026-03-03T00:00:00Z"
 */
export const buildUtcIsoDateOnly = (date: Date): string => {
  // Create pure UTC date at midnight without any timezone influence
  // Use local date components (not UTC) to preserve the user's selected date
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  ));
  
  const result = utcDate.toISOString();  
  return result;  // Returns: 2026-03-03T00:00:00.000Z
};

/**
 * Formats a Date object to YYYY-MM-DD using local date components
 * This preserves the visual date without timezone conversion
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 * @example
 * const date = new Date("2026-03-03");
 * formatDateForInput(date); // Returns: "2026-03-03"
 */
export const formatDateForInput = (date: Date | null): string => {
  if (!date) return "";
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formats a UTC date string to readable format WITHOUT timezone conversion
 * Uses date-fns-tz to format the date in UTC timezone
 * @param dateString - UTC date string (e.g., "2026-03-03T11:00:00.000Z")
 * @returns Formatted date string in "dd LLL yyyy, hh:mm:ss a" format
 * @example
 * formatUtcDateWithoutTimezone("2026-03-03T11:00:00.000Z") 
 * // Returns: "03 Mar 2026, 11:00:00 AM"
 */
export const formatUtcDateWithoutTimezone = (dateString: string | Date | null | undefined): string | Date | null | undefined => {
  if (!dateString) return dateString;
  return formatInTimeZone(new Date(dateString), "UTC", "dd LLL yyyy, hh:mm:ss a");
};

export const formatText = (text: any) => {
  if (text)
    return text
      .toLowerCase()
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char: any) => char.toUpperCase())// Capitalize first letter of each word
      .replace(' Us', ' US')
  else
    return text;
};

export const formatCurrency = (amount: number) => {
  return (amount / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

/**
 * Converts a timestamp to Date, handling both milliseconds and seconds
 */
export const convertTimestampToDate = (timestamp: number): Date => {
  if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
    return new Date();
  }

  const isMilliseconds = timestamp.toString().length >= 13;
  const date = new Date(isMilliseconds ? timestamp : timestamp * 1000);

  const year = date.getFullYear();
  if (isNaN(date.getTime()) || year < 2020 || year > 2030) {
    return new Date();
  }

  return date;
};

export const formatDateToLocal = (
  dateStr: string,
  locale: string = "en-GB"
) => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(date);
};

/**
 * Format date to long format (e.g., "July 23, 2026")
 * Used in subscription details and webhook notifications
 */
export const formatDateLong = (dateStr: string | Date, locale: string = "en-US") => {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return date.toLocaleDateString(locale, options);
};

export const generateYAxis = (revenue: Revenue[]) => {
  // Calculate what labels we need to display on the y-axis
  // based on highest record and in 1000s
  const yAxisLabels = [];
  const highestRecord = Math.max(...revenue.map((month) => month.revenue));
  const topLabel = Math.ceil(highestRecord / 1000) * 1000;

  for (let i = topLabel; i >= 0; i -= 1000) {
    yAxisLabels.push(`$${i / 1000}K`);
  }

  return { yAxisLabels, topLabel };
};

export const generatePagination = (currentPage: number, totalPages: number) => {
  // If the total number of pages is 7 or less,
  // display all pages without any ellipsis.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // If the current page is among the first 3 pages,
  // show the first 3, an ellipsis, and the last 2 pages.
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages - 1, totalPages];
  }

  // If the current page is among the last 3 pages,
  // show the first 2, an ellipsis, and the last 3 pages.
  if (currentPage >= totalPages - 2) {
    return [1, 2, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  // If the current page is somewhere in the middle,
  // show the first page, an ellipsis, the current page and its neighbors,
  // another ellipsis, and the last page.
  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

export function formatEpochToDateTime(epochMs: number, timezone: string = 'UTC'): string {
  const date = new Date(epochMs);
  
  const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
  };
  
  return date.toLocaleString('en-US', options);
}

// Formats the classStartTime/classEndTime epochs on each entry of an
// employee.training array into the given timezone — shared by
// applicantService.getApplicantsById and ATS listOnboardingApplications,
// which both surfaced the same inline map before. Epochs are normalized
// (Amazon payloads have carried both seconds and milliseconds) and a
// missing timezone falls back to UTC.
export function formatTrainingClassTimes(training: any[], timeZone: string | null) {
  const tz = timeZone || "UTC";
  const formatField = (value: unknown) => {
    const ms = normalizeEpochToMs(value);
    return ms != null ? formatEpochToDateTime(ms, tz) : value;
  };
  return training.map((item: any) => ({
    ...item,
    classStartTime: item.classStartTime
      ? formatField(item.classStartTime)
      : item.classStartTime,
    classEndTime: item.classEndTime
      ? formatField(item.classEndTime)
      : item.classEndTime,
  }));
}

export const normalizeEpochToMs = (raw: unknown): number | null => {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isNaN(n)) return null;
  if (n > 1e14) return Math.round(n / 1000); // microseconds
  if (n < 1e12) return Math.round(n * 1000); // seconds
  return Math.round(n); // already milliseconds
};

export const convertEpochTimeAsPerTimeZone = (
  epochTime: string | number,
  timeZone: string
) => {
  // Parse epochTime as number
  const epochNum = typeof epochTime === "string" ? parseFloat(epochTime) : epochTime;

  // Determine if epochNum is in seconds (10 digits or less) or milliseconds (13 digits or more)
  // If in seconds, convert to milliseconds by multiplying by 1000
  const epochTimeMs = epochNum < 1e12 ? epochNum * 1000 : epochNum;

  // Create a Date object
  const date = new Date(epochTimeMs);

  // Specify the time zone and include date and time components
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
  };

  // Format the date
  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
  return formattedDate;
};

// Converts a UTC timestamp into "Amazon data as of 7:09pm · 3m ago".
export const formatLastUpdated = (iso: string, timeZone: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const timeStr = convertEpochTimeAsPerTimeZone(date.getTime(), timeZone)
    .replace(/\s/g, "")
    .toLowerCase();
  const relative = formatDistanceToNow(date, { addSuffix: true });
  return `Amazon data as of ${timeStr} · ${relative}`;
};

/**
 * Converts epoch time to full datetime string in the specified timezone
 * Returns format: "YYYY-MM-DD HH:MM AM/PM"
 */
export const convertEpochToFullDateTime = (
  epochTime: string | number,
  timeZone: string
) => {
  // Parse epochTime as number
  const epochNum = typeof epochTime === "string" ? parseFloat(epochTime) : epochTime;

  // Determine if epochNum is in seconds (10 digits or less) or milliseconds (13 digits or more)
  // If in seconds, convert to milliseconds by multiplying by 1000
  const epochTimeMs = epochNum < 1e12 ? epochNum * 1000 : epochNum;

  // Create a Date object
  const date = new Date(epochTimeMs);

  // Format with date and time components
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  // Format the full datetime
  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);
  
  // Extract parts and reconstruct as YYYY-MM-DD HH:MM AM/PM
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  const hour = parts.find(p => p.type === 'hour')?.value;
  const minute = parts.find(p => p.type === 'minute')?.value;
  const period = parts.find(p => p.type === 'dayPeriod')?.value;

  return `${year}-${month}-${day} ${hour}:${minute} ${period}`;
};


// Function specifically for converting milliseconds to time format
export const convertMillisecondsToTime = (
  milliseconds: string | number,
  timeZone: string = 'UTC',
  format: '12hour' | '24hour' = '12hour'
) => {
  // Parse milliseconds as number
  const ms = typeof milliseconds === "string" ? parseFloat(milliseconds) : milliseconds;
  
  // Create Date object from milliseconds
  const date = new Date(ms);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  // Configure options based on format preference
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: format === '12hour'
  };

  // Format and return the time
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

/**
 * Checks if the current time is after or equal to the given clock-in time
 * @param clockInTime - Time string in format "h:mm AM/PM" (e.g., "9:00 AM")
 * @returns boolean - true if current time is greater than or equal to clock-in time
 */
export const isCurrentTimeAfterClockIn = (clockInTime: string): boolean => {
  if (!clockInTime || clockInTime === "N/A") return false;
  return !isBefore(new Date(), parse(clockInTime, "h:mm a", new Date()));
};

/**
 * Helper function to parse time strings to minutes since midnight
 * @param timeStr - Time string in format "h:mm AM/PM" (e.g., "10:45 AM" or "9:03 PM")
 * @returns number - Minutes since midnight, or null if invalid
 */
export const parseTimeToMinutes = (timeStr: string | null | undefined): number | null => {
  if (!timeStr || typeof timeStr !== 'string' || timeStr.toLowerCase() === 'n/a') {
    return null;
  }

  // Match format like "10:45 AM" or "9:03 PM"
  const parts = timeStr.match(/(\d+):(\d{2})\s*(AM|PM)/i);
  if (parts && parts.length === 4) {
    try {
      let hours = parseInt(parts[1], 10);
      const minutes = parseInt(parts[2], 10);
      const period = parts[3].toUpperCase();

      // Handle AM/PM conversion to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12; // Convert PM hours to 24-hour format (e.g., 9 PM → 21)
      } else if (period === 'AM' && hours === 12) {
        hours = 0; // Handle midnight (12 AM → 0)
      } else if (period === 'PM' && hours === 12) {
        // 12 PM stays as 12
      }

      // Validate hours and minutes
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        console.error(`Invalid time values: ${timeStr} (hours: ${hours}, minutes: ${minutes})`);
        return null;
      }

      return hours * 60 + minutes;
    } catch (e) {
      console.error(`Failed to parse time string: ${timeStr}`, e);
      return null;
    }
  }

  console.error(`Invalid time format: ${timeStr}`);
  return null;
};

/**
 * Helper function to format minutes into a readable "Xh Ym" string
 * @param totalMinutes - Total minutes to format
 * @returns string - Formatted string like "2h 30m" or "N/A" if invalid
 */
export const formatMinutesToHoursMinutes = (totalMinutes: number | null | undefined): string => {
  try {
    if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes) || totalMinutes < 0) {
      return 'N/A';
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error(`Failed to format minutes to hours and minutes: ${totalMinutes}`, error);
    return 'N/A';
  }
};

export const convertToUserTimezone = (
  utcDateTime: string,
  userTimeZone: string
) => {
  // Parse the UTC date-time
  const utcDate = DateTime.fromISO(utcDateTime, { zone: "utc" });

  // console.log("convertToUserTimeZone", utcDate);

  // Check if the parsed date is valid
  if (!utcDate.isValid) {
    throw new Error(`Invalid date format: ${utcDateTime}`);
  }

  // Convert to the user's timezone
  const userDate = utcDate.setZone(userTimeZone);

  // Format the date-time explicitly with AM/PM
  return userDate.toFormat("dd LLL yyyy, hh:mm:ss a");
};

/**
 * Gets UTC offset in minutes for a given timezone
 *
 * This function is used to dynamically compare timezones without requiring a static mapping.
 * It enables the system to determine if the browser timezone matches the service area timezone
 * by comparing their UTC offsets, which is necessary for accurate NO_DATA record insertion.
 *
 * Without this, we would need to maintain a mapping of equivalent timezones (e.g., US/Eastern = America/New_York),
 * which is not scalable and prone to errors.
 *
 * @param tz - Timezone string (e.g., "Asia/Kolkata", "America/New_York", "US/Eastern")
 * @param date - Date to calculate offset for (defaults to current time)
 * @returns UTC offset in minutes (e.g., 330 for IST, -240 for EDT)
 */
export const getTimezoneOffset = (tz: string, date: Date = new Date()): number => {
  // Use date-fns-tz to get UTC offset in format "+0530" or "-0400"
  const offset = formatInTimeZone(date, tz, 'xx');
  const hours = parseInt(offset.substring(0, 3));
  const minutes = parseInt(offset.substring(3, 5));
  return hours * 60 + minutes;
};

// export function urlBase64ToUint8Array(base64String: string) {
//   const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
//   const base64 = (base64String + padding)
//     .replace(/\\-/g, "+")
//     .replace(/_/g, "/");

//   const rawData = window.atob(base64);
//   const outputArray = new Uint8Array(rawData.length);

//   for (let i = 0; i < rawData.length; ++i) {
//     outputArray[i] = rawData.charCodeAt(i);
//   }
//   return outputArray;
// }

export const defaultMenuItems = [
  {
    name: "Driver",
    path: "/driver",
    icon: QrCodeIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/driver"),
    children: [
      {
        name: "Inspection",
        path: "/driver/inspection",
      },
      {
        name: "Chat - Dispatch",
        path: "/driver/chat-dispatch",
      },
      {
        name: "Chat - HR",
        path: "/driver/chat-hr",
      },
      {
        name: "Bag Order",
        path: "/driver/bag-order",
      },
      {
        name: "My Schedule",
        path: "/driver/schedule",
      },
      {
        name: "RTS",
        path: "/driver/rts",
      },
      {
        name: "Fleet Documents",
        path: "/driver/fleet-documents",
      },
      {
        name: "Write-ups",
        path: "/driver/write-ups",
      },
      {
        name: "Performance",
        path: "/driver/performance",
      },
      {
        name: "Coaching",
        path: "/driver/coaching",
      },      
      {
        name: "Corrective Action",
        path: "/driver/corrective-action",
      },
      {
        name: "Incident",
        path: "/driver/incident",
      },
      {
        name: "Compliance",
        path: "/driver/compliance",
      },
      {
        name: "Unplanned Stops",
        path: "/driver/unplanned-stops",
      }
    ],
  },
  {
    name: "Chat",
    path: "/chat",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/chat"),
    children: [
      {
        name: "Dispatch",
        path: "/chat/dispatch",
      },
      {
        name: "HR",
        path: "/chat/hr",
      },
    ],
  },
  // ATS module — top-level entry matching the Performance/Schedule pattern.
  // WHY top-level: ATS is a full module (candidates, pipeline, dashboard), not a
  // settings sub-page. Each module in this codebase gets its own sidebar entry.
  // The pages under /ats check useExperimentalAccess() and show a "coming
  // soon" message for orgs not in EXPERIMENTAL_ORG_IDS, so this menu item is
  // safe to ship to all orgs — only experimental orgs see real content.
  {
    name: "ATS",
    path: "/ats",
    icon: ClipboardDocumentCheckIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/ats"),
    children: [
      // Interviews is first because it's the most-used workflow (scheduling
      // slots and managing bookings). Pipeline is second (candidate tracking).
      // Settings is last (configuration — questionnaires, short links, etc.).
      {
        name: "Interviews",
        path: "/ats/interviews",
      },
      {
        name: "Pipeline",
        path: "/ats/pipeline",
      },
      {
        name: "Settings",
        path: "/ats/settings",
      },
    ],
  },
  {
    name: "Associates",
    path: "/associates",
    icon: ComputerDesktopIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/associates"),
    children: [
      {
        name: "Onboarding Applicants",
        path: "/associates/onboarding-applicants",
      },
      {
        name: "Active Employee",
        path: "/associates/employee",
      },
      {
        name: "Employee Report",
        path: "/associates/employee-report",
      },
    ],
  },
  {
    name: "Schedule",
    path: "/schedule",
    icon: ClockIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/schedule"),
    children: [
      {
        name: "Schedule Admin",
        path: "/schedule/admin",
      },
      {
        name: "Dispatch",
        path: "/schedule/dispatch",
      },
      {
        name: "EOD Summary",
        path: "/schedule/eod-summary",
      },
      {
        name: "Settings",
        path: "/schedule/settings",
      },
    ],
  },
  {
    name: "Operations",
    path: "/operation",
    icon: MagnifyingGlassIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/operation"),
    children: [
      {
        name: "Dashboard",
        path: "/operation/dashboard",
      },
      {
        name: "Delivery",
        path: "/operation/delivery",
      },
      {
        name: "Tips",
        path: "/operation/tips",
      },
      // NOTE: Anomalies menu item is temporarily hidden as per request
      // {
      //   name: "Anomalies",
      //   path: "/operation/anomalies",
      // },
      {
        name: "Incident",
        path: "/operation/incident",
      },
      {
        name: "Package",
        path: "/operation/package",
      },
    ],
  },
  {
    name: "Asset",
    path: "/asset",
    icon: DeviceTabletIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/asset"),
    children: [
      {
        name: "Mobile",
        path: "/asset/mobile",
      },
      {
        name: "Toll Tag",
        path: "/asset/tolltag",
      },
      {
        name: "Gas Card",
        path: "/asset/gascard",
      },
    ]
  },
  {
    name: "Fleet",
    path: "/fleet-management",
    // suggest a icon which seems like van or vehicle from current library
    icon: TruckIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/fleet-management"),
    children: [
      {
        name: "Dashboard",
        path: "/fleet-management/dashboard",
      },
      {
        name: "Vehicle",
        path: "/fleet-management/vehicle",
      },
      {
        name: "Inspection",
        path: "/fleet-management/inspection",
      },
      {
        name: "Fleet Repair",
        path: "/fleet-management/fleet-repair",
      },
    ],
  },
  {
    name: "Performance",
    path: "/performance",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/performance"),
    children: [
      {
        name: "Dashboard",
        path: "/performance/dashboard",
      },
      {
        name: "Leaderboard",
        path: "/performance/leaderboard",
      },
      {
        name: "Safety",
        path: "/performance/safety",
      },
      {
        name: "CDF",
        path: "/performance/customer-delivery-feedback-negative",
      },
      {
        name: "DSB",
        path: "/performance/delivery-success-behaviour",
      },
      {
        name: "DCR",
        path: "/performance/delivery-completion-rate",
      },
      {
        name: "DVIC",
        path: "/performance/driver-vehicle-inspection-checklist",
      },
      {
        name: "Coaching",
        path: "/performance/coaching",
      },
      {
        name: "Corrective Action",
        path: "/performance/corrective-action",
      },
      {
        name: "Settings",
        path: "/performance/settings",
      }
    ]
  },
  {
    name: "Payroll",
    path: "/payroll",
    icon: CurrencyDollarIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/payroll"),
    children: [
      {
        name: "Timecard",
        path: "/payroll/timecard",
      },
      {
        name: "Termination",
        path: "/payroll/termination",
      },
      {
        name:"Payroll ID",
        path:"/payroll/payroll-id"
      },
      {
        name:"Compliance",
        path:"/payroll/compliance"
      },
      {
        name:"Compliance Rules",
        path:"/payroll/compliance-rules"
      },
      {
        name:"Unplanned Stops",
        path:"/payroll/unplanned-stops"
      },
    ],
  },
  {
    name: "Invoice",
    path: "/invoice",
    icon: DocumentTextIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/invoice"),
    children: [
      {
        name: "Fleet",
        path: "/invoice/fleet",
      },
      {
        name: "Fleet 3P",
        path: "/invoice/fleet-3p",
      },
      // NOTE: UPD menu item is temporarily hidden as per request
      // {
      //   name: "UPD",
      //   path: "/invoice/upd",
      // },
      {
        name: "Variable",
        path: "/invoice/variable",
      },      
      {
        name: "Insights",
        path: "/invoice/insights",
      },
      {
        name: "Daily Operation",
        path: "/invoice/daily-operation",
      },
    ],
  },
  {
    name: "Reports",
    path: "/reports",
    icon: ReceiptRefundIcon,
    activeCondition: (pathname: string) =>
      pathname.includes("reports"),
    children: [
      {
        name: "Attendance",
        path: "/reports/attendance",
      },
    ],
  },
  {
    name: "Settings",
    path: "/settings",
    icon: Cog8ToothIcon,
    activeCondition: (pathname: string) => 
      pathname.includes("/settings") || 
      pathname.includes("/integrations") || 
      pathname.includes("/users") || 
      pathname.includes("/subscription") || 
      pathname.includes("/organization-config") ||
      pathname.includes("/templates"),
    children: [
      {
        name: "Integrations",
        path: "/integrations",
      },
      {
        name: "Templates",
        path: "/templates",
      },
      {
        name: "Users",
        path: "/users",
      },
      {
        name: "Subscription",
        path: "/subscription",
      },
      {
        name: "Organization",
        path: "/organization-config",
      },
    ],
  },
  {
    name: "Admin",
    path: "/admin",
    icon: Cog8ToothIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/admin"),
    children: [
      {
        name: "Chat",
        path: "/admin/chat",
      },
      {
        name: "Help Desk",
        path: "/admin/help-desk",
      },
      {
        name: "Onboarding",
        path: "/admin/onboarding-org",
      }
    ],
  },
  {
    name: "Help",
    path: "/help",
    icon: QuestionMarkCircleIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/help") || pathname.startsWith("/install"),
    children: [
      {
        name: "Install",
        path: "/install",
      },
      {
        name: "Contact",
        path: "/help/contact",
      },
    ],
  }
];

// Define pages that should default to "All" selection
export const defaultAllPages = [
  "/fleet-management/vehicle",
];

export const driverMenuItems = [
  {
    name: "Inspection",
    path: "/driver/inspection",
    icon: MagnifyingGlassIcon,
    activeCondition: (pathname: string) => pathname.includes("inspection"),
  },
  {
    name: "Chat - Dispatch",
    path: "/driver/chat-dispatch",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.includes("chat-dispatch"),
  },
  {
    name: "Chat - HR",
    path: "/driver/chat-hr",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.includes("chat-hr"),
  },
  {
    name: "Bag Order",
    path: "/driver/bag-order",
    icon: EnvelopeIcon,
    activeCondition: (pathname: string) => pathname.includes("bag-order"),
  },
  {
    name: "My Schedule",
    path: "/driver/schedule",
    icon: ClockIcon,
    activeCondition: (pathname: string) => pathname.includes("schedule"),
  },
  {
    name: "RTS",
    path: "/driver/rts",
    icon: TruckIcon,
    activeCondition: (pathname: string) => pathname.includes("rts"),
  },
  {
    name: "Fleet Documents",
    path: "/driver/fleet-documents",
    icon: DocumentIcon,
    activeCondition: (pathname: string) => pathname.includes("fleet-documents"),
  },
  {
    name: "Write-ups",
    path: "/driver/write-ups",
    icon: PencilSquareIcon,
    activeCondition: (pathname: string) => pathname.includes("write-ups"),
  },
  {
    name: "Performance",
    path: "/driver/performance",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.includes("performance"),
  },
  {
    name: "Coaching",
    path: "/driver/coaching",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.includes("coaching"),
  },
  {
    name: "Corrective Action",
    path: "/driver/corrective-action",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.includes("corrective-action"),
  },
  {
    name: "Incident",
    path: "/driver/incident",
    icon: ExclamationTriangleIcon,
    activeCondition: (pathname: string) => pathname.includes("/driver/incident"),
  },
  {
    name: "Compliance",
    path: "/driver/compliance",
    icon: ExclamationTriangleIcon,
    activeCondition: (pathname: string) => pathname.includes("compliance"),
  },
  {
    name: "Unplanned Stops",
    path: "/driver/unplanned-stops",
    icon: MapPinIcon,
    activeCondition: (pathname: string) => pathname.includes("unplanned-stops"),
  },
];

export const applicantMenuItems = [
  {
    name: "Application Status",
    path: "/applicant/application-status",
    icon: ClipboardDocumentCheckIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/applicant/application-status"),
  },
  {
    name: "Chat",
    path: "/applicant/chat",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/applicant/chat"),
  },
  {
    name: "Install",
    path: "/install",
    icon: DeviceTabletIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/install"),
  },
  {
    name: "Contact",
    path: "/help/contact",
    icon: QuestionMarkCircleIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/help/contact"),
  },
];

/**
 * Sorts and converts a list of van objects.
 * 
 * This function takes an array of objects containing a `vehicleName` property,
 * filters out any entries with null or empty `vehicleName`, sorts the remaining
 * names numerically if possible, and then alphanumerically, and finally converts
 * them back into an array of objects.
 */

export function sortAndConvertVanList(vanList: { vehicleName: string | null, serviceType: string | null }[]): { vehicleName: string | null, serviceType: string | null }[] {
  return vanList
    .filter((van) => van.vehicleName !== null && van.vehicleName !== "") // Ensure non-null and non-empty vehicleName
    .sort((a: any, b: any) => {
      const numA = Number(a.vehicleName);
      const numB = Number(b.vehicleName);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB; // Numeric sorting
      return a.vehicleName.localeCompare(b.vehicleName, undefined, { numeric: true })
    });
}

export function generateRandomPassword(length: number = 12): string {
  // Ensure length is within valid range (8-32)
  const validLength = Math.max(8, Math.min(32, length));
  
  // Character sets matching password validation requirements
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numbers = "0123456789";
  const specialChars = "@$!%*?&#"; // Only allowed special characters
  const allChars = lowercase + uppercase + numbers + specialChars;
  
  // Guarantee at least one character from each required set
  let password = "";
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += specialChars[Math.floor(Math.random() * specialChars.length)];
  
  // Fill remaining length with random characters from all sets
  for (let i = password.length; i < validLength; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password to avoid predictable pattern (first 4 chars always same types)
  password = password.split('').sort(() => Math.random() - 0.5).join('');
  
  return password;
}

export const formatUTCTo12Hour = (utcString: string) => {
  const date = new Date(utcString); // Convert UTC string to Date object

  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Month is 0-based
  const day = date.getUTCDate().toString().padStart(2, '0');

  // Extract UTC time components
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12; // Convert 0 to 12 for AM/PM format
  const formattedMinutes = minutes.toString().padStart(2, '0'); // Ensure 2-digit minutes

  // Format: YYYY-MM-DD HH:MM AM/PM
  return `${year}-${month}-${day} ${hours}:${formattedMinutes} ${ampm}`;
};

export interface GenerateS3FileNameParams {
  organizationId: number | string; // Support both number and string (UUID)
  source: string; // Source context (e.g. "fleet-management", "associates")
  identifier?: string | null; // Additional identifier like vehicle VIN or employee ID
  inspectionType?: string | null; // Type of inspection (e.g. "pre-inspection", "post-inspection", "pickup-inspection", "return-inspection")
  fieldKey: string; // Field identifier for the file
  includeRandomChars?: boolean; // Whether to include random characters in the filename
  includeDateString?: boolean; // Whether to include date string in the filename
}

export async function generateS3FileName({
  organizationId,
  source,
  identifier,
  inspectionType,
  fieldKey,
  includeRandomChars = true,
  includeDateString = true,
}: GenerateS3FileNameParams): Promise<string> {
  const today = new Date();
  // Generate date string only if includeDateString is true
  const dateString = includeDateString ? today.toISOString().slice(0, 10).replace(/-/g, '') : null;
  
  // Generate random characters only if includeRandomChars is true
  const randomChars = includeRandomChars ? Math.random().toString(36).substring(2, 10) : null;

  // Build the path components
  const pathComponents = [
    organizationId,
    source,
    ...(identifier ? [identifier] : []),
    ...(inspectionType ? [inspectionType] : []),
    // Include date string only if includeDateString is true
    ...(dateString ? [dateString] : []),
    // Include random characters in fieldKey only if includeRandomChars is true
    randomChars ? `${fieldKey}-${randomChars}` : fieldKey,
  ];
  return pathComponents.join('/');
}

export const convertMillisecondsToMinutesSeconds = (
  ms: number,
  showSign: boolean = true
) => {
  const isNegative = ms < 0;
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const sign = showSign ? (isNegative ? '-' : '+') : '';

  const paddedSeconds = String(seconds).padStart(2, '0');
  return `${sign}${minutes}:${paddedSeconds} mins`;
};

// Convert minutes to hours and minutes format (H:MM)
export const convertMinutesToHoursMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const paddedMinutes = String(remainingMinutes).padStart(2, '0');

  return `${hours}:${paddedMinutes} hrs`;
};

// Format display name
export const formatDisplayName = (value: string): string => {
  // Convert camelCase or snake_case to Title Case With Spaces
  return value
    .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/-/g, ' ') // Replace hyphens with spaces
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

export const removeQueryParamsFromPreSignedUrl = (url: string): string => {
  const index = url.indexOf("?");
  return index === -1 ? url : url.substring(0, index);
};

export const getS3KeyFromPresignedUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname.replace(/^\/+/, ""); // remove leading slash
  } catch (error) {
    throw new Error("Invalid S3 URL");
  }
};


export const readCSVFile = async (file: File): Promise<any[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      // First, read the file content as text to preprocess it
      const fileContent = await file.text();
      
      // Clean up the CSV content:
      // 1. Remove trailing spaces after quotes at the end of lines
      // 2. Ensure proper line endings
      // 3. Remove any trailing whitespace at the end of the file
      const cleanedContent = fileContent
        .split('\n')
        .map(line => {
          // Remove trailing spaces after quotes at the end of each line
          return line.replace(/"\s+$/g, '"').trim();
        })
        .filter(line => line.length > 0) // Remove empty lines
        .join('\n')
        .trim(); // Remove any trailing whitespace from the entire file
      
      // Parse the cleaned content directly
      Papa.parse(cleanedContent, {
        header: true,
        skipEmptyLines: 'greedy',
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        transformHeader: (header) => header.trim(),
        transform: (value) => value.trim(),
        complete: (results) => {
          // Filter out only critical errors, ignore quote formatting issues if data is still parseable
          const criticalErrors = results.errors.filter(error => 
            error.code !== 'InvalidQuotes' && 
            error.code !== 'MissingQuotes' &&
            error.code !== 'TooFewFields' &&
            error.code !== 'TooManyFields'
          );
          
          if (criticalErrors.length > 0) {
            console.error('Critical CSV parsing errors:', criticalErrors);
            reject(criticalErrors[0].message);
            return;
          }
          
          // Log non-critical errors for debugging but continue processing
          if (results.errors.length > 0) {
            console.warn('Non-critical CSV parsing warnings:', results.errors);
          }
          
          // Normalize headers to lowercase for consistent schema validation
          // This handles cases where users upload CSVs with capitalized headers (e.g., "Name", "Provider ID")
          // Backend Zod schemas expect lowercase headers (e.g., "name", "provider id")
          const normalizedData = results.data.map((record: any) => {
            const normalized: any = {};
            Object.keys(record).forEach((key) => {
              normalized[key.toLowerCase()] = record[key];
            });
            return normalized;
          });
          
          resolve(normalizedData);
        },
        error: (error: any) => {
          console.error('CSV parsing error:', error);
          reject(error.message);
        }
      });
    } catch (error: any) {
      console.error('Error preprocessing CSV file:', error);
      reject(`Error preprocessing CSV file: ${error}`);
    }
  });
};

/**
 * DYNAMIC extraction - extracts vehicle type keyword from service type string
 * Handles TWO patterns:
 * PATTERN 1: [Service Prefix] - [VEHICLE TYPE] - [Additional Info]
 *   Examples: "Standard Parcel - Custom Delivery Van 16ft" → "custom delivery van"
 *             "WW Hub Transport - Step Van - US" → "step van"
 * PATTERN 2: [Service Name] ([VEHICLE TYPE])
 *   Examples: "DSP Initiated Work (Standard Vehicle)" → "standard vehicle"
 *             "DSP Initiated Work (Step Van)" → "step van"
 */
/**
 * Normalize a cycle value to the underscore key format used in workBlocks.
 * Handles both "cycle" and "amxl" prefixes:
 *   "Cycle 1" -> "cycle_1", "AMXL 1" -> "amxl_1", "cycle_1" -> "cycle_1"
 * Falls back to "cycle_1" when the input is missing.
 * @param rawCycle The cycle value to normalize (e.g. "Cycle 1", "amxl 2", "cycle_0")
 * @returns Normalized cycle key (e.g. "cycle_1", "amxl_2")
 */
export function normalizeCycleKey(rawCycle: string | null | undefined): string {
  if (!rawCycle) return 'cycle_1';
  let cycle = rawCycle.toLowerCase();
  if (cycle.includes('cycle') && !cycle.includes('_')) {
    cycle = cycle.replace(/cycle\s*(\d+)/i, 'cycle_$1');
  } else if (cycle.includes('amxl') && !cycle.includes('_')) {
    cycle = cycle.replace(/amxl\s*(\d+)/i, 'amxl_$1');
  }
  return cycle;
}

/**
 * Format cycle keys for display: amxl_* -> AMXL_*, cycle_* -> Cycle_*
 * @param cycleKey The cycle key to format (e.g. "cycle_1", "amxl_2")
 * @returns Formatted cycle name for display
 */
export const formatCycleKeyForDisplay = (cycleKey: string): string => {
  if (!cycleKey) return '';
  const lower = cycleKey.toLowerCase();
  if (lower.startsWith('amxl')) {
    return cycleKey.toUpperCase();
  }
  if (lower.startsWith('cycle')) {
    return cycleKey.charAt(0).toUpperCase() + cycleKey.slice(1).toLowerCase();
  }
  return cycleKey.replace(/([A-Z])/g, ' $1').trim();
};

export const extractVehicleTypeKeyword = (serviceType: string | null): string | null => {
  if (!serviceType) return null;
  
  // PATTERN 1: [Service Prefix] - [VEHICLE TYPE] - [Additional Info]
  const segments = serviceType.split(' - ');
  if (segments.length >= 2) {
    let vehicleType = segments[1].trim().toLowerCase();
    
    // Remove size specifications (16ft, 12ft, etc.) for flexible matching
    vehicleType = vehicleType.replace(/\s*\d+ft\b/gi, '').trim();
    
    // Remove capacity indicators like (large), (medium), (small)
    vehicleType = vehicleType.replace(/\s*\([^)]*\)\s*/g, '').trim();
    
    if (vehicleType && vehicleType.length > 0) {
      return vehicleType;
    }
  }
  
  // PATTERN 2: [Service Name] ([VEHICLE TYPE])
  // Example: "DSP Initiated Work (Standard Vehicle)"
  const parenMatch = serviceType.match(/\(([^)]+)\)/);
  if (parenMatch) {
    let vehicleType = parenMatch[1].trim().toLowerCase();
    
    // Remove size specifications (16ft, 12ft, etc.) for flexible matching
    vehicleType = vehicleType.replace(/\s*\d+ft\b/gi, '').trim();
    
    // Remove capacity indicators like (large), (medium), (small)
    vehicleType = vehicleType.replace(/\s*\([^)]*\)\s*/g, '').trim();
    
    if (vehicleType && vehicleType.length > 0) {
      return vehicleType;
    }
  }
  
  return null;
};

// Function to parse Excel files (XLS/XLSX)
export const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          // Parses to JSON, using headers in first row
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });

          // Normalize headers to lowercase for consistent schema validation
          // This handles cases where users upload Excel files with capitalized headers (e.g., "EE_Name", "Position ID")
          // Backend Zod schemas expect lowercase headers (e.g., "ee_name", "position id")
          const normalizedRows = rows.map((record: any) => {
            const normalized: any = {};
            Object.keys(record).forEach((key) => {
              normalized[key.toLowerCase()] = record[key];
            });
            return normalized;
          });

          resolve(normalizedRows);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };


export const compressImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Only compress images
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      const MAX_WIDTH = 1920; // Max width for the image
      const MAX_HEIGHT = 1600; // Max height for the image

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      // Set canvas dimensions to the optimized size
      canvas.width = width;
      canvas.height = height;

      // Draw image onto canvas with smoothing
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob with optimized quality
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file from blob
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg", // Convert to JPEG for better compression
                lastModified: Date.now(),
              });

              resolve(compressedFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg", // Use JPEG format
          0.9 // 90% quality - good balance between quality and compression
        );
      } else {
        reject(new Error("Failed to get canvas context"));
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Converts seconds to hours:minutes format
 * @param seconds - Total seconds to convert
 * @returns Formatted string in "H:MM" or "HH:MM" format
 */
export function convertSecondsToHoursMinutes(seconds: number): string {
  // Handle negative values
  const absoluteSeconds = Math.abs(seconds);
  
  // Calculate hours and minutes
  const hours = Math.floor(absoluteSeconds / 3600);
  const minutes = Math.floor((absoluteSeconds % 3600) / 60);
  
  // Format with leading zero for minutes
  const formattedMinutes = minutes.toString().padStart(2, '0');
  
  // Return formatted string
  return `${hours}:${formattedMinutes} hrs`;
}

export function epochMillisecondsToYYYYMMDDdate(epochTime: number, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return formatter.format(new Date(epochTime));
}

/**
 * Fetches and stores subscription data in IndexedDB (CLIENT-SIDE ONLY)
 * @param organizationId The organization ID
 */
export const fetchAndStoreSubscriptionData = async (organizationId: string): Promise<void> => {
  try {
    if (!organizationId || typeof window === 'undefined') {
      return;
    }

    // Make API call to fetch subscription data
    const response = await fetch('/api/subscriptions/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pathname: '/' }),
    });

    if (!response || !response.ok) {
      console.error('Failed to fetch subscription data:', response?.statusText);
      return;
    }

    const data = await response.json();
    
    // Extract result from the new API response structure
    const result = data.result;
    
    if (result && result.planId && result.accessiblePaths) {
      // Store subscription data in IndexedDB
      await setSubscriptionData({
        subscriptionPlanId: result.planId,
        subscriptionPlanName: result.planName,
        accessiblePaths: result.accessiblePaths || []
      });
    }
  } catch (error) {
    console.error("Error fetching and storing subscription data:", error);
  }
};

/**
 * Determines the appropriate redirect path based on user role
 * Used by both login and signup flows
 */
export const determineRedirectPath = async (user: any, setOrganizationName: (name: string) => void, fetchUserDetails: () => Promise<void>, isPWA: boolean) => {
  let path = '/';  
  const response:any = await fetchUserDetails();

  if(user?.organizationName){
    setOrganizationName(user.organizationName);
  }
  // Fetch subscription data and store in IndexedDB
  try {
    if (user?.organizationId) {
      await fetchAndStoreSubscriptionData(user.organizationId);
    }
  } catch (error) {
    console.error("Error fetching subscription data:", error);
  }

  if(isPWA){
  path = response?.defaultPage;
  }else{
  path = "/install";
  }

 return path;
};

/**
 * Array of path prefixes where the service area dropdown should NOT be shown
 * Add new path prefixes here to exclude them from showing the service area dropdown
 */
export const serviceAreaDropdownExcludedPaths = [
  '/driver/',      // Paths starting with /driver/
  '/applicant/',   // Paths starting with /applicant/
  '/settings',     // Paths starting with /settings
  '/install',      // Paths starting with /install
  '/help',         // Help pages (instructional / support)
  '/add-organization', // Paths starting with /add-organization
  '/operation/dashboard', 
  '/operation/anomalies', 
  '/operation/tips', 
  '/operation/clinics',
  '/subscription', // Paths starting with /subscription
  '/announcement',
  '/integrations',
  '/templates',
  '/fleet-management/dashboard',
  '/payroll/compliance-rules',
  '/users',
  '/schedule/settings',
  '/admin/announcement',
  '/admin/chat',
  '/admin/help-desk',
  '/admin/onboarding-org'
];

/**
 * Paths allowed when organization has no active subscription
 * Users without a subscription can only access these paths
 */
export const noSubscriptionAllowedPaths = [
  '/install',           // Browser extension installation guide
  '/help/contact',      // Help desk / support contact
  '/subscription',      // Plans and subscription management
  '/onboarding',        // Onboarding flow
  '/plan-restricted'    // Plan-restricted path
];

/**
 * Only Admin Paths that are excluded from PROVIDER_LINQOPS_ADMIN role validation
 * These are system-level paths that should not require PROVIDER_LINQOPS_ADMIN role restrictions:
 * - api/admin/cron/*: API endpoint for cron jobs
 * - api/admin/webhook/*: API endpoint triggered by Amazon to add employees to the system
 */
export const adminExcludedPaths = [
  'api/admin/cron/*',
  'api/admin/webhook/*'
]

/**
 * Handles 402 Payment Required responses by validating subscription data
 * and updating IndexedDB if necessary
 * 
 * @param response The API response object (with 402 status)
 * @param organizationId The organization ID
 * @param pathname The current pathname
 * @returns Object with updated subscription access status
 */
export async function handleSubscriptionRequired(
  response: Response,
  pathname: string
): Promise<{ hasAccess: boolean; currentPlan: string | null }> {
  // Only proceed if this is a 402 Payment Required response
  if (response.status !== 402) {
    return { hasAccess: true, currentPlan: null };
  }

  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return { hasAccess: false, currentPlan: null };
    }

    // Import dynamically to avoid SSR issues
    const { getSubscriptionData, setSubscriptionData } = await import('./indexedDB');

    // Call the validation API to get the latest subscription data
    const validateResponse = await fetch('/api/subscriptions/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pathname }),
    });

    if (!validateResponse.ok) {
      console.error('Failed to validate subscription:', validateResponse.statusText);
      return { hasAccess: false, currentPlan: null };
    }

    const { result } = await validateResponse.json();
    
    // Get current subscription data from IndexedDB
    const currentData = await getSubscriptionData();
    
    // Check if the data needs to be updated
    const needsUpdate = !currentData || 
      JSON.stringify(currentData.accessiblePaths) !== JSON.stringify(result.accessiblePaths);
    
    // Update IndexedDB if needed
    if (needsUpdate) {
      await setSubscriptionData({
        subscriptionPlanId: result.planId,
        subscriptionPlanName: result.planName,
        accessiblePaths: result.accessiblePaths || [],
      });
    }

    // Return the updated access status
    return { 
      hasAccess: result.hasAccess, 
      currentPlan: result.planName 
    };
  } catch (error) {
    console.error('Error handling subscription required:', error);
    return { hasAccess: false, currentPlan: null };
  }
}

export function getWeekNumberFromEpoch(epochMs: any): number | null {
  const week = moment.utc(epochMs, "YYYY-MM-DD").isoWeek();
  return week;
}

/**
 * Week option interface for dropdown selections
 */
export interface WeekOption {
  label: string;        // "Week 1" or "2025 – Week 52"
  value: string;        // "week-1"
  startDate: string;    // "2026-01-01"
  endDate: string;      // "2026-01-07"
  weekNumber?: number;  // 1 (optional, for future use)
  year?: number;        // 2026 (optional, for future use)
}

/**
 * Generates week options for dropdown with proper ISO week-year formatting
 * 
 * Weeks belonging to the current ISO week-year display as "Week 1", "Week 2", etc.
 * Weeks belonging to previous ISO week-years display as "2025 - Week 52", "2025 - Week 51", etc.
 * 
 * Important: Uses ISO week-year logic, not calendar year. For example:
 * - Dec 29, 2025 is part of Week 1 of 2026 (ISO week-year 2026), so it displays as "Week 1"
 * - This ensures weeks are labeled based on which year they belong to according to ISO standards
 */
export function generateWeekOptions(
  todayInUserTz: Date,
  maxWeeksBack = 12
): WeekOption[] {
  const options: WeekOption[] = [];

  let cursorDate = todayInUserTz;
  
  // Get the current ISO week-year (which year the current week belongs to)
  const currentWeekYear = getWeekYear(todayInUserTz, {
    weekStartsOn: 0,
    firstWeekContainsDate: 1,
  });

  for (let i = 0; i < maxWeeksBack; i++) {
    const weekStart = startOfWeek(cursorDate, {
      weekStartsOn: 0,
    });

    const weekEnd = endOfWeek(weekStart, {
      weekStartsOn: 0,
    });

    const weekNumber = getWeek(cursorDate, {
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    });

    // Get the ISO week-year for this week (which year this week belongs to)
    const weekYear = getWeekYear(cursorDate, {
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    });

    // If the week belongs to the current ISO week-year, show "Week X"
    // Otherwise, show "YYYY - Week X" with the ISO week-year
    const label =
      weekYear === currentWeekYear
        ? `Week ${weekNumber}`
        : `Week ${weekNumber}, ${weekYear}`;

    options.push({
      label,
      value: `week-${weekYear}-${weekNumber}`,
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd'),
      weekNumber,
      year: weekYear,
    });

    cursorDate = subWeeks(cursorDate, 1);
  }

  return options;
}

/**
 * Month option interface for dropdown selections
 */
export interface MonthOption {
  label: string;        // "January" or "2025 - December"
  value: string;        // "0", "1", "2", etc. (month number as string)
  startDate: string;    // "2026-01-01"
  endDate: string;      // "2026-01-31"
  monthNumber?: number; // 0-11 (January = 0)
  year?: number;        // 2026
}

/**
 * Generates month options for dropdown with proper year formatting
 * 
 * Months belonging to the current calendar year display as "January", "February", etc.
 * Months belonging to previous calendar years display as "2025 - December", "2025 - November", etc.
 * 
 * @param todayInUserTz - Current date in user's timezone
 * @param maxMonthsBack - Number of months to go back (default: 12)
 * @param minSelectableDate - Minimum selectable date (e.g., organization start date). Dates before this will not be included.
 * @returns Array of month options with labels, values, and date ranges
 */
export function generateMonthOptions(
  todayInUserTz: Date,
  maxMonthsBack = 12,
  minSelectableDate?: Date
): MonthOption[] {
  const options: MonthOption[] = [];
  
  // Get current year
  const currentYear = todayInUserTz.getFullYear();
  
  // Start from current month
  let cursorDate = todayInUserTz;
  
  for (let i = 0; i < maxMonthsBack; i++) {
    const monthDate = subMonths(cursorDate, i);
    
    const monthNumber = monthDate.getMonth(); // 0-11
    const year = monthDate.getFullYear();
    const monthName = format(monthDate, 'MMMM');
    
    // Calculate start and end dates
    const startDate = startOfMonth(monthDate);
    const endDate = endOfMonth(monthDate);
    
    // SAFEGUARD: Skip this month if it's before the minimum selectable date
    // This ensures we don't include months that start before the organization was created
    if (minSelectableDate && endDate < minSelectableDate) {
      continue;
    }
    
    // If the month belongs to the current year, show "January"
    // Otherwise, show "2025 - December" with the year
    const label = year === currentYear
      ? monthName
      : `${monthName}, ${year}`;
    
    options.push({
      label,
      value: `${year}-${monthNumber}`,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      monthNumber,
      year,
    });
  }
  
  return options;
}

/**
 * Gets date range for trailing 6 weeks from today
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function getTrailing6WeeksDateRange() {
  const today = new Date();
  // Calculate 6 weeks ago (42 days)
  const sixWeeksAgo = new Date(today);
  sixWeeksAgo.setDate(today.getDate() - 42);

  // Format dates as YYYY-MM-DD
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(sixWeeksAgo),
    endDate: formatDate(today)
  };
}

export function normalizePhoneNumber(phone: string | null): string | null {
  if (!phone) return null;

  // Already in E.164 format
  if (phone.startsWith('+')) {
    return phone;
  }

  // If it's 10 digits, assume it's US and add +1
  if (/^\d{10}$/.test(phone)) {
    return `+1${phone}`;
  }

  return null; // invalid format
}

/**
 * Format phone number for tel: links
 * Removes all non-digit characters and returns plain digits with +1 prefix
 * This format works correctly with tel: links on mobile devices
 * 
 * @param phoneNumber - Raw phone number string (can be in any format)
 * @returns Formatted phone number for tel: link (e.g., +18452003703) or null if invalid
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber) return null;

  // Remove non-digits
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Match 10 digits OR 11 digits starting with 1
  if (cleaned.length === 10) return `+1${cleaned}`;
  if (cleaned.length === 11 && cleaned.startsWith("1")) return `+${cleaned}`;

  return null;
}

export function getTodayInTimezone(timezone: string, baseDate: Date = new Date()) {
  const zoned = toZonedTime(baseDate, timezone);
  return startOfDay(zoned);
}

/**
 * - Returns the offboarded date string (YYYY-MM-DD) based on the calling source.
 * - Used in fetchDSPAssociatesMapper.ts (batch sync) and getOperationalStatusByProviderId route (webhook/API).
 * - Centralizes the offboarded date logic so different sources can use different date strategies
 *   (current date, week-end, etc.) without duplicating logic or timezone handling across the codebase.
 */
export function getAmazonOffboardedDate(source: string): string {
  const today = new Date().toISOString().split('T')[0];
  switch (source) {
    // Batch sync from Amazon DSP associates - use current date when employee is detected as offboarded
    case AMAZON_OFFBOARDED_SOURCE.FETCH_DSP_ASSOCIATES:
      return today;
    // Webhook/API status update from Amazon - use current date when offboarded status is pushed
    case AMAZON_OFFBOARDED_SOURCE.GET_OPERATIONAL_STATUS_BY_PROVIDER_ID:
      return today;
    // Default fallback - use current week's last date (Saturday) for future sources or manual operations
    default:
      return format(endOfWeek(new Date(), { weekStartsOn: 0 }), 'yyyy-MM-dd');
  }
}

/**
 * Date range picker option interface (shared with DateRangePicker component)
 */
export interface DateRangePickerOption {
  label: string;
  value: string;
  startDate: string;
  endDate: string;
}

/**
 * Generates week, month, and year options for DateRangePicker from minimum selectable date to current date
 * 
 * This function generates all three option types needed for the DateRangePicker component:
 * - Week options: From minimum selectable date through current date, using ISO week-year formatting
 * - Month options: Unique months derived from week options
 * - Year options: Unique years derived from week options
 * 
 * @param todayInUserTz - Current date in user's timezone
 * @param minSelectableDate - Minimum selectable date (e.g., organization start date). Defaults to 2025-01-01.
 * @returns Object containing weekOptions, monthOptions, and yearOptions arrays
 */
export function generateDateRangePickerOptions(
  todayInUserTz: Date,
  minSelectableDate?: Date
): {
  weekOptions: DateRangePickerOption[];
  monthOptions: DateRangePickerOption[];
  yearOptions: DateRangePickerOption[];
} {
  // Use provided minSelectableDate or default to 2025-01-01
  const effectiveMinDate = minSelectableDate || new Date(2025, 0, 1);
  const maxWeeksBack = Math.ceil(
    (todayInUserTz.getTime() - effectiveMinDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
  ) + 2;

  // Generate week options from January 2025 to current date
  const weekOpts = generateWeekOptions(todayInUserTz, maxWeeksBack).map((option) => ({
    label: option.label,
    value: option.value,
    startDate: option.startDate,
    endDate: option.endDate,
  }));

  // Generate month options from week options (unique months)
  const monthMap = new Map<string, DateRangePickerOption>();
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  weekOpts.forEach((week) => {
    const date = new Date(`${week.startDate}T00:00:00`);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    if (!monthMap.has(monthKey)) {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      monthMap.set(monthKey, {
        label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
        value: monthKey,
        startDate: formatLocalDate(monthStart),
        endDate: formatLocalDate(monthEnd),
      });
    }
  });
  const monthOptions = Array.from(monthMap.values()).reverse();

  // Generate year options from week options (unique years)
  const yearMap = new Map<number, DateRangePickerOption>();
  weekOpts.forEach((week) => {
    const date = new Date(`${week.startDate}T00:00:00`);
    if (!yearMap.has(date.getFullYear())) {
      const yearStart = new Date(date.getFullYear(), 0, 1);
      const yearEnd = new Date(date.getFullYear(), 11, 31);
      yearMap.set(date.getFullYear(), {
        label: String(date.getFullYear()),
        value: String(date.getFullYear()),
        startDate: formatLocalDate(yearStart),
        endDate: formatLocalDate(yearEnd),
      });
    }
  });

  // Also add the year from minSelectableDate if it's not already included
  // This ensures the organization's creation year is always available
  if (minSelectableDate) {
    const minYear = effectiveMinDate.getFullYear();
    if (!yearMap.has(minYear)) {
      const yearStart = new Date(minYear, 0, 1);
      const yearEnd = new Date(minYear, 11, 31);
      yearMap.set(minYear, {
        label: String(minYear),
        value: String(minYear),
        startDate: formatLocalDate(yearStart),
        endDate: formatLocalDate(yearEnd),
      });
    }
  }

  const yearOptions = Array.from(yearMap.values()).reverse();

  return { weekOptions: weekOpts, monthOptions, yearOptions };
}

/**
 * Given a date in 'yyyy-mm-dd' format, returns the second-to-last day of the previous month.
 * Example: '2025-01-01' → '2024-12-30'
 * @param dateString - Date in 'yyyy-mm-dd' format
 * @returns Second-to-last day of the previous month in 'yyyy-mm-dd' format
 */
export function getSecondLastDayOfPreviousMonth(dateString: string): string {
  // Parse the input date
  const inputDate = parseISO(dateString);
  
  // Get the previous month
  const previousMonth = subMonths(inputDate, 1);
  
  // Get the last day of the previous month
  const lastDay = lastDayOfMonth(previousMonth);
  
  // Get the second-to-last day (subtract 1 day)
  const secondLastDay = subDays(lastDay, 1);
  
  // Return in 'yyyy-mm-dd' format
  return format(secondLastDay, 'yyyy-MM-dd');
}

/**
 * Validates integration credentials for both Paycom and ADP
 * Uses constants from INTEGRATION_CREDENTIAL_FIELDS
 * 
 * @param integrationType - Type of integration ('paycom' or 'adp')
 * @param credentials - Credentials object
 * @param certificatePem - Certificate PEM string (for ADP only)
 * @param privateKey - Private key string (for ADP only)
 * @param requireAllFields - Whether to require all fields (default: true)
 * @returns Validation result with isValid, missingFields, and detailed validation info
 */
export function validateIntegrationCredentials(
  integrationType: string,
  credentials: Record<string, any>,
  certificatePem?: string | null,
  privateKey?: string | null,
  requireAllFields: boolean = true
): { 
  isValid: boolean; 
  missingFields: string[]; 
  isPartial: boolean;
  certValid?: boolean;
  keyValid?: boolean;
} {
  const missingFields: string[] = [];

  if (integrationType === INTEGRATIONS_TYPE.PAYCOM) {
    // Validate Paycom credentials
    const requiredFields = INTEGRATION_CREDENTIAL_FIELDS.PAYCOM.CREDENTIALS;
    requiredFields.forEach(field => {
      if (!credentials[field]) {
        missingFields.push(field);
      }
    });
  } else if (integrationType === INTEGRATIONS_TYPE.ADP) {
    // Validate ADP credentials
    const credentialFields = INTEGRATION_CREDENTIAL_FIELDS.ADP.CREDENTIALS;
    const certificateFields = INTEGRATION_CREDENTIAL_FIELDS.ADP.CERTIFICATES;
    
    // Check basic credentials
    credentialFields.forEach(field => {
      if (!credentials[field]) {
        missingFields.push(field);
      }
    });
    
    // Validate certificate format if provided
    let certValid = false;
    if (certificatePem && certificatePem.trim() !== '') {
      certValid = certificatePem.includes("BEGIN CERTIFICATE") && certificatePem.includes("END CERTIFICATE");
    }
    
    // Validate private key format if provided
    let keyValid = false;
    if (privateKey && privateKey.trim() !== '') {
      keyValid = privateKey.includes("BEGIN PRIVATE KEY") && privateKey.includes("END PRIVATE KEY");
    }
    
    // Add certificate fields to missing if required but not provided or invalid
    if (requireAllFields) {
      if (!certificatePem || !certValid) missingFields.push('certificate_pem');
      if (!privateKey || !keyValid) missingFields.push('private_key');
    }
    
    return { 
      isValid: missingFields.length === 0 && certValid && keyValid, 
      missingFields, 
      isPartial: false,
      certValid, 
      keyValid 
    };
  } else if (integrationType === INTEGRATIONS_TYPE.UZIO) {
    // Validate Uzio credentials using the same pattern as Paycom
    // Uzio only requires an apiKey, so we check it via the shared INTEGRATION_CREDENTIAL_FIELDS constant
    const requiredFields = INTEGRATION_CREDENTIAL_FIELDS.UZIO.CREDENTIALS;
    requiredFields.forEach(field => {
      if (!credentials[field]) {
        missingFields.push(field);
      }
    });
  }
  
  return { 
    isValid: missingFields.length === 0, 
    missingFields, 
    isPartial: false 
  };
}

/**
 * Convert a string to title case (first letter capitalized, rest lowercase)
 * Example: "BUDGET" -> "Budget", "budget" -> "Budget"
 */
export const toTitleCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Normalize provider name to title case while preserving suffix
 * Example: "BUDGET - RENTAL" -> "Budget - RENTAL"
 * Only the provider part (before the dash) is converted to title case
 * Exception: "LP" provider is left unchanged
 */
export const normalizeProviderName = (providerName: string): string => {
  if (!providerName) return providerName;
  
  const parts = providerName.split(' - ');
  if (parts.length >= 2) {
    // Get the provider part
    const provider = parts[0];
    
    // If provider is "LP", leave it unchanged
    if (provider.toUpperCase() === 'LP') {
      const suffix = parts.slice(1).join(' - '); // Preserve everything after first dash
      return `${provider} - ${suffix}`;
    }
    
    // Convert other providers to title case, keep suffix as-is
    const normalizedProvider = toTitleCase(provider);
    const suffix = parts.slice(1).join(' - '); // Preserve everything after first dash
    return `${normalizedProvider} - ${suffix}`;
  }
  
  // If no dash found, check if it's "LP" (leave unchanged) or convert to title case
  if (providerName.toUpperCase() === 'LP') {
    return providerName;
  }
  
  // If no dash found, just convert to title case
  return toTitleCase(providerName);
};

/**
 * Generates Amazon Logistics Portal itinerary URL for a driver
 * 
 * @param itineraryId - The itinerary ID from itinerarySummaries table
 * @param date - The date in 'yyyy-MM-dd' format
 * @param serviceAreaId - The service area ID
 * @returns The complete Amazon portal URL with itineraryId if available, otherwise base URL
 */
export const generateAmazonItineraryUrl = (
  itineraryId: string | null | undefined,
  date: string,
  serviceAreaId: string
): string => {
  const baseUrl = `https://logistics.amazon.com/operations/execution/itineraries`;
  const queryParams = `?navMenuVariant=external&provider=ALL_DRIVERS&selectedDay=${date}&serviceAreaId=${serviceAreaId}`;

  if (!itineraryId) {
    return `${baseUrl}${queryParams}`;
  }

  return `${baseUrl}/${itineraryId}/documentType/Itinerary${queryParams}`;
};

/**
 * Generates Amazon route view URL with route ID and query parameters
 * @param routeId - The route ID from routeSummaries
 * @param date - The date in 'yyyy-MM-dd' format
 * @param serviceAreaId - The service area ID
 * @returns The complete Amazon route view URL
 */
export const generateAmazonRouteUrl = (
  routeId: string | null | undefined,
  date: string,
  serviceAreaId: string
): string => {
  const baseUrl = `https://logistics.amazon.com/operations/execution/dv/routes`;
  const queryParams = `?navMenuVariant=external&provider=ALL_DRIVERS&selectedDay=${date}&serviceAreaId=${serviceAreaId}&transporter=ALL_DRIVERS`;

  if (!routeId) {
    return `${baseUrl}${queryParams}`;
  }

  return `${baseUrl}/${routeId}${queryParams}`;
};

/**
 * Checks if an employee record has null package data (packagesDelivered and flexLogOut)
 * Used for filtering employees with null data when they have exclusion notes
 * 
 * @param record The employee record to check
 * @returns boolean - true if both packagesDelivered AND flexLogOut are null
 * 
 * @example
 * // Check if employee has null package data
 * if (!hasNullData(record)) {
 *   return true; // Keep employees with valid data
 * }
 */
export function hasNullData(record: any): boolean {
  // Check specifically for the two required fields: packagesDelivered and flexLogOut
  const hasNoFlexLogOut = !record.flexLogOut || record.flexLogOut === null;
  const hasNoPackagesDelivered = !record.packagesDelivered || record.packagesDelivered === null;
  
  // Only return true if BOTH packagesDelivered AND flexLogOut are null
  // This is the specific requirement for filtering employees
  return hasNoFlexLogOut && hasNoPackagesDelivered;
}

/**
 * Generic search function for performance pages that searches through employee names and tracking IDs.
 * This function works with any data structure that has employees with nested date groups containing records with tracking IDs.
 * 
 * @param data Array of employee data objects
 * @param searchTerm The search term to filter by
 * @returns Filtered array of employee data
 * 
 * @example
 * // Usage in DSB, CDF, DCR pages
 * const filteredData = applyPerformanceSearch(data, searchTerm);
 */
export function applyPerformanceSearch<T extends {
  employeeName: string;
  dateGroups: Array<{
    records: Array<{
      trackingId?: string | null;
    }>;
  }>;
}>(data: T[], searchTerm: string): T[] {
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return data.filter(employee => {
    // Search in employee name (existing functionality) - show all data
    if (employee.employeeName?.toLowerCase().includes(lowerSearchTerm)) {
      return true;
    }

    // Search in tracking IDs from nested records (new functionality)
    const hasMatchingTrackingId = employee.dateGroups.some(dateGroup =>
      dateGroup.records.some(record =>
        record.trackingId?.toLowerCase().includes(lowerSearchTerm)
      )
    );

    return hasMatchingTrackingId;
  }).map(employee => {
    // If searching by employee name, return full employee data
    if (employee.employeeName?.toLowerCase().includes(lowerSearchTerm)) {
      return employee;
    }

    // If searching by tracking ID, filter to show only matching date groups and records
    const filteredDateGroups = employee.dateGroups.map(dateGroup => ({
      ...dateGroup,
      records: dateGroup.records.filter(record =>
        record.trackingId?.toLowerCase().includes(lowerSearchTerm)
      )
    })).filter(dateGroup => dateGroup.records.length > 0);

    return {
      ...employee,
      dateGroups: filteredDateGroups
    };
  });
}


/**
 * Check if a target path is accessible based on accessible paths list
 * Handles both exact matches and wildcard patterns
 * @param accessiblePaths Array of accessible path patterns
 * @param targetPath The path to check access for
 * @returns Boolean indicating if path is accessible
 */
export function checkPathAccess(
  accessiblePaths: string[],
  targetPath: string
): boolean {
  if (!accessiblePaths || accessiblePaths.length === 0) {
    return false;
  }

  return accessiblePaths.some((path: string) => {
    if (path.endsWith("/*")) {
      // Wildcard pattern: "/subscription/*" matches "/subscription" and "/subscription/anything"
      const basePath = path.slice(0, -2);
      return targetPath.startsWith(basePath);
    }
    // Exact match: "/subscription" === "/subscription"
    return path === targetPath;
  });
}

export const drawPdfGeneratedByFooter = (
  pdfDoc: PDFDocument,
  font: PDFFont,
  options?: {
    rightMargin?: number;
    bottomMargin?: number;
    fontSize?: number;
    label?: string;
  },
): void => {
  const rightMargin = options?.rightMargin ?? 20;
  const bottomMargin = options?.bottomMargin ?? 20;
  const fontSize = options?.fontSize ?? 8;
  const label = options?.label ?? "LinqOps";

  const currentDate = format(new Date(), "dd MMM yyyy, hh:mm:ss a");
  const footerText = `Report Generated By: ${label}, ${currentDate}`;
  const footerTextWidth = font.widthOfTextAtSize(footerText, fontSize);

  for (const page of pdfDoc.getPages()) {
    const { width } = page.getSize();
    page.drawText(footerText, {
      x: Math.max(rightMargin, width - rightMargin - footerTextWidth),
      y: bottomMargin,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    });
  }
};

/**
 * Shared password validation schema used across the application
 * Requirements:
 * - Minimum 8 characters
 * - Maximum 32 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character (@$!%*?&#)
 */
export const passwordValidationSchema = z
  .string()
  .min(8, "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).")
  .max(32, "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).")
  .regex(/[A-Z]/, "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).")
  .regex(/[a-z]/, "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).")
  .regex(/[0-9]/, "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).")
  .regex(
    /[@$!%*?&#]/,
    "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#)."
  );

/**
 * Converts decimal hours to H:MM format with sign handling
 * Examples: 10.083 -> "10:05", -2.5 -> "-2:30"
 * @param h - Decimal hours to convert (can be negative)
 * @param includeSuffix - Whether to include " hrs" suffix (default: false)
 * @returns Formatted time string in H:MM format
 */
export function fmtHM(h: number, includeSuffix: boolean = false): string {
  const sign = h < 0 ? "-" : "";
  const a = Math.abs(h);
  const H = Math.floor(a);
  const M = Math.round((a - H) * 60);
  const HH = M === 60 ? H + 1 : H;
  const MM = M === 60 ? 0 : M;
  const suffix = includeSuffix ? " hrs" : "";
  return `${sign}${HH}:${String(MM).padStart(2, "0")}${suffix}`;
}

/**
 * Validates a password string against the standard password requirements
 * @param password - The password string to validate
 * @returns Object with success boolean and optional error message
 */
export const validatePassword = (
  password: string
): { success: boolean; error?: string } => {
  try {
    passwordValidationSchema.parse(password);
    return { success: true };
  } catch (error) {
    // Return all requirements instead of individual error
    return { 
      success: false, 
      error: "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#)." 
    };
  }
};

/**
 * Password validation requirements as a human-readable message
 */
export const PASSWORD_REQUIREMENTS = "Password must be 8-32 characters, including uppercase, lowercase, a number, and a special character (@$!%*?&#).";

/**
 * Password validation criteria for UI display
 */
export const passwordCriteria = [
  { id: 'length', label: '8-32 characters', test: (pwd: string) => pwd.length >= 8 && pwd.length <= 32 },
  { id: 'uppercase', label: 'Uppercase letter', test: (pwd: string) => /[A-Z]/.test(pwd) },
  { id: 'lowercase', label: 'Lowercase letter', test: (pwd: string) => /[a-z]/.test(pwd) },
  { id: 'number', label: 'Number', test: (pwd: string) => /[0-9]/.test(pwd) },
  { id: 'special', label: 'Special character (@$!%*?&#)', test: (pwd: string) => /[@$!%*?&#]/.test(pwd) },
];

/**
 * Dummy email addresses for non-production Documenso testing
 * These are used when creating/signing documents in non-production environments
 * to avoid sending real emails to actual users
 */
export const DUMMY_SUPERVISOR_EMAIL = "test+supervisor@linqops.com";
export const DUMMY_EMPLOYEE_EMAIL = "test+employee@linqops.com";

/**
 * Returns the appropriate email for Documenso recipients based on environment
 * 
 * In production: returns the actual email as-is
 * In non-production:
 *   - If actual email contains @linqops.com: returns the actual email (use real linqops emails for testing)
 *   - Otherwise: returns the dummy test email based on role
 * 
 * This allows testing with real linqops emails in non-prod while using dummy emails for external domains
 * 
 * @param actualEmail - The actual email address of the user
 * @param role - The role of the user ('supervisor' or 'employee')
 * @returns The email address to use for Documenso
 */
export function getTestEmail(actualEmail: string, role: 'supervisor' | 'employee'): string {
  // Check both server-side (STAGE) and client-side (NEXT_PUBLIC_STAGE) environment variables
  const stage = process.env.STAGE || process.env.NEXT_PUBLIC_STAGE || "";
  const isProd = stage === "production";
  
  // In production, always use the actual email
  if (isProd) {
    return actualEmail;
  }
  
  // In non-production, use actual email if it's a linqops email
  // This allows testing with real linqops accounts
  const normalizedEmail = String(actualEmail || "").trim().toLowerCase();
  if (normalizedEmail.includes("@linqops.com")) {
    return normalizedEmail;
  }
  
  // Otherwise, use the dummy test email based on role
  return role === 'supervisor' ? DUMMY_SUPERVISOR_EMAIL : DUMMY_EMPLOYEE_EMAIL;
}

/**
 * Checks if an email is the dummy supervisor email
 * Used in webhook handlers to identify when a dummy email signed a document
 * 
 * @param email - The email to check
 * @returns True if the email is the dummy supervisor email
 */
export function isDummySupervisorEmail(email: string): boolean {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail === DUMMY_SUPERVISOR_EMAIL;
}

/**
 * Checks if an email is the dummy employee email
 * Used in webhook handlers to identify when a dummy email signed a document
 * 
 * @param email - The email to check
 * @returns True if the email is the dummy employee email
 */
export function isDummyEmployeeEmail(email: string): boolean {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail === DUMMY_EMPLOYEE_EMAIL;
}

/**
 * Classify a URL as image, pdf, or other based on file extension.
 *
 * NOTE: The default return was changed from "image" to "other" because
 * presigned S3 URLs do not contain file extensions. The old default of
 * "image" caused PDFs (and all unknown files) to be misclassified as
 * images, leading to broken <img> rendering. Now unknown URLs default
 * to "other", and components like ShowUploadedDoc use runtime detection
 * (img onError → iframe fallback) instead of relying on this function.
 */
export function classifyUrl(url: string): "image" | "pdf" | "other" {
  const lower = String(url || "").toLowerCase();
  if (lower.includes(".pdf")) return "pdf";
  if (lower.includes(".png") || lower.includes(".jpg") || lower.includes(".jpeg")) return "image";
  return "other";
}

/**
 * Format vehicle name with VIN in the format "Vehicle Name (VIN)"
 */
export function formatVehicleNameWithVin(vehicleName: string | null, vin: string | null): string | null {
  try {
    if (!vehicleName || !vin) {
      return null;
    }
    return `${vehicleName} (${vin})`;
  } catch (error) {
    console.error("[formatVehicleNameWithVin] Error formatting vehicle name:", error);
    return null;
  }
}

/**
 * Return the UTC epoch seconds for the next midnight in the given IANA timezone.
 */
export function getNextMidnightInTimezone(timeZone: string): number {
  const nowInTz = toZonedTime(new Date(), timeZone);
  const nextMidnightInTz = addDays(startOfDay(nowInTz), 1);
  const nextMidnight = fromZonedTime(nextMidnightInTz, timeZone);
  return Math.floor(nextMidnight.getTime() / 1000);
}

/**
 * Type guard for a FollowUpRule. Uses the Zod schema defined in
 * atsPipelineValidator so the validation logic stays in one place.
 * Filters malformed JSONB rules before they enter React state or the
 * manual send flow.
 */
export function isValidFollowUpRule(r: unknown): r is FollowUpRule {
  return followUpRuleSchema.safeParse(r).success;
}

/**
 * Does a stage config have enabled, usable follow-up rules? Pure JSONB check
 * kept out of SQL — shared by the cron enqueue filter and the automation
 * service so both agree on what "has follow-up rules" means. At least one rule
 * must validate (a stage of only-malformed rules produces no sends anyway).
 */
export function hasEnabledFollowUpRules(stageConfig: unknown): boolean {
  const followUpRules = (
    stageConfig as { followUpRules?: { enabled?: boolean; rules?: unknown[] } } | null
  )?.followUpRules;
  return (
    followUpRules?.enabled === true &&
    Array.isArray(followUpRules.rules) &&
    followUpRules.rules.some(isValidFollowUpRule)
  );
}

// Build the ATS-specific path segment that comes after the orgId in Firebase.
// This is the "chatId" — the part that distinguishes ATS threads from
// Dispatch ({employeeId}) and HR ({employeeId}-hr) threads under the same
// chats/{orgId}/ root. Extracted as a separate helper because
// hasExistingMessages(organizationId, chatId) takes the segment, not the full path.
export function getAtsChatId(applicationId: string): string {
  return `${ATS_CHAT_FIREBASE_PREFIX}/${applicationId}`;
}

// Build the full Firebase RTDB path for an ATS chat thread.
// DRY: previously the path template `chats/${orgId}/ats/${applicationId}` was
// hardcoded in 4 files (2 API routes, 1 SMS service, 1 client service). A
// single builder here ensures the path format stays consistent — if the
// structure ever changes, only this function needs updating.
// Lives in utils.ts (not shared.ts) so both client and server can import it
// without pulling server-side dependencies (db, drizzle) into client bundles.
export function getAtsChatPath(organizationId: string, applicationId: string): string {
  return `chats/${organizationId}/${getAtsChatId(applicationId)}`;
}

  /**
 * Format a stored pay rate value with the organization's currency symbol.
 *
 * Pay rates are stored as raw numeric strings (e.g. "23.50"). Legacy data may
 * have a "$" baked into the stored value (e.g. "$23.50") — this helper strips
 * any non-numeric characters before formatting so both old and new data render
 * correctly.
 *
 * @param value  - The stored pay rate (e.g. "23.50" or legacy "$23.50"), or null
 * @param currency - The org's currency symbol (e.g. "€", "£"). Falls back to "$" when null/empty.
 * @returns Formatted string like "$23.50" or "€23.50", or null when value is empty
 */
export function formatPayRate(
  value: string | null | undefined,
  currency?: string | null | undefined,
): string | null {
  if (!value) return null;
  const symbol = currency || "$";
  const numericValue = value.replace(/[^0-9.]/g, "");
  return numericValue ? `${symbol}${numericValue}` : null;
}

// ============================================================================
// Shared RTS helpers (moved from rtsUtils.ts)
// Used by rtsService/packageService (driver + ops APIs) and the RTS UIs
// ============================================================================

const PRESERVED_BOX_TYPES = ["SIOC", "VBOD"];

export function normalizeBoxType(boxType?: string | null): string | null {
  if (!boxType) return null;
  const suffix = boxType.replace(/^BOX_TYPE_/, "").replace(/_/g, " ").trim();
  const suffixUpper = suffix.toUpperCase();
  if (PRESERVED_BOX_TYPES.includes(suffixUpper)) return suffixUpper;
  return suffix
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Pick the most useful human-readable status for a task.
// taskStateContext is preferred when set (e.g., "OUT_FOR_DELIVERY"), except for
// PICKUP_FAILED, where the explicit failure state matters more to the driver.
export function getTaskStatus(task: any): string {
  if (task?.taskState === "PICKUP_FAILED") return String(task.taskState);
  if (task?.taskStateContext && task.taskStateContext !== "NONE") {
    return String(task.taskStateContext);
  }
  if (task?.taskState) return String(task.taskState);
  return "UNKNOWN";
}

// Handle both route-details (domainMap) and execution-itinerary (root) shapes.
export function getScannableId(task: any): string | null {
  return task?.domainMap?.scannableId ?? task?.scannableId ?? null;
}

// Scan stops/tasks for the given query and populate packagesMap.
// Reused for itinerarySummaries and the routeSummaries fallback.
export function collectPackageMatches(
  rows: any[],
  query: string,
  packagesMap: Map<string, PackageMatchEntry>,
): void {
  for (const row of rows) {
    const stops = Array.isArray(row.stops) ? row.stops : [];
    const routeCode = row.routeCode ?? null;
    const rowTransporterId = row.transporterId ?? null;
    const stopsUpdatedAt = row.stopsUpdatedAt ?? null;

    for (const stop of stops) {
      const stopNumber = stop?.sequenceNumber ?? null;
      const tasks = Array.isArray(stop?.tasks) ? stop.tasks : [];

      for (const task of tasks) {
        const scannableId = getScannableId(task);
        if (!scannableId || typeof scannableId !== "string") continue;

        const upperId = scannableId.toUpperCase();
        if (!upperId.includes(query)) continue;

        const existing = packagesMap.get(upperId);
        // Skip sequence-1 duplicates if we already have a non-sequence-1 task for this TBA.
        if (existing && stopNumber === 1) continue;
        // If we only had sequence 1 before and the current task is not sequence 1, replace it.
        if (existing && existing.stopNumber !== 1) continue;

        packagesMap.set(upperId, {
          task,
          stop,
          stopNumber,
          routeCode,
          transporterId: rowTransporterId,
          stopsUpdatedAt,
        });
      }
    }
  }
}

// ============================================================================
// RTS UI-side helpers (moved from driver/rts/find-package/page.tsx)
// ============================================================================

// Converts raw task status (e.g., "OUT_FOR_DELIVERY") to a readable label.
export const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\btr\b/g, "TR")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function getEventVariant(taskState: string): TimelineEvent["variant"] {
  switch (taskState) {
    case "DELIVERED":
      return "success";
    case "PICKED_UP":
      return "info";
    case "YOU_ARE_NEXT":
      return "warning";
    case "PICKUP_FAILED":
      return "error";
    default:
      return "neutral";
  }
}

// Builds the shared timeline: recentTaskEvents + planned stop + departure.
// Extracted from the driver find-package page; identical output.
export function buildPackageTimelineEvents(
  pkg: PackageTimelineSource,
  timeZone: string,
): TimelineEvent[] {
  const sources: {
    id: string;
    epoch: number;
    title: string;
    description?: string;
    variant: TimelineEvent["variant"];
  }[] = [];

  if (pkg.recentTaskEvents && pkg.recentTaskEvents.length > 0) {
    pkg.recentTaskEvents.forEach((evt, idx) => {
      const epoch = normalizeEpochToMs(evt.executionTime);
      if (epoch == null) return;

      const title =
        evt.taskState === "PICKUP_FAILED"
          ? formatStatus(evt.taskState)
          : evt.taskStateContext && evt.taskStateContext !== "NONE"
            ? formatStatus(evt.taskStateContext)
            : formatStatus(evt.taskState);
      const description =
        evt.taskState === "PICKUP_FAILED" &&
        evt.taskStateContext &&
        evt.taskStateContext !== "NONE"
          ? formatStatus(evt.taskStateContext)
          : undefined;
      sources.push({
        id: `${pkg.scannableId}-evt-${idx}`,
        epoch,
        title,
        description,
        variant: getEventVariant(evt.taskState),
      });
    });
  }

  const plannedEndEpoch = normalizeEpochToMs(pkg.plannedEndTime);
  if (plannedEndEpoch) {
    sources.push({
      id: `${pkg.scannableId}-planned`,
      epoch: plannedEndEpoch,
      title: `Stop #${pkg.stop ?? "?"} was planned`,
      description: "Planned delivery time",
      variant: "info",
    });
  }

  const actualDepartureEpoch = normalizeEpochToMs(pkg.actualDepartureTime);
  if (actualDepartureEpoch) {
    const plannedDepartureEpoch = normalizeEpochToMs(pkg.plannedDepartureTime);
    let description = pkg.isOwnRoute
      ? "You departed"
      : "Route left the station";
    if (plannedDepartureEpoch) {
      const diffMs = actualDepartureEpoch - plannedDepartureEpoch;
      const minutes = Math.round(Math.abs(diffMs) / (60 * 1000));
      const planTime = convertEpochTimeAsPerTimeZone(
        plannedDepartureEpoch,
        timeZone,
      );
      if (minutes === 0) {
        description = `On time for the ${planTime} plan`;
      } else if (diffMs < 0) {
        description = `${minutes} min ahead of the ${planTime} plan`;
      } else {
        description = `${minutes} min behind the ${planTime} plan`;
      }
    }

    sources.push({
      id: `${pkg.scannableId}-departed`,
      epoch: actualDepartureEpoch,
      title: pkg.isOwnRoute ? "You departed" : "Route departed",
      description,
      variant: "info",
    });
  }

  return sources
    .sort((a, b) => a.epoch - b.epoch)
    .map((s) => ({
      ...s,
      time: convertEpochTimeAsPerTimeZone(s.epoch, timeZone),
    }));
}

// ============================================================================
// Driver RTS confirmation flow (D1) helpers
// ============================================================================

// Amazon's recorded reason for a return: taskStateContext is the reason,
// taskState is the bucket. NONE/missing context = no recorded reason.
export function deriveAmazonReason(
  task: any,
): { label: string; raw: string } | null {
  const state = task?.taskState ?? null;
  const ctx = task?.taskStateContext ?? null;
  if (!state) return null;
  const raw = ctx ? `${state} / ${ctx}` : state;
  if (!ctx || ctx === "NONE") return null;
  return { label: formatStatus(ctx), raw };
}

// A package is expected back when its delivery task is in any non-delivered
// state — BACK_TO_ORIGIN, PICKUP_FAILED, or a not-attempted/pending state.
export function isExpectedReturnTask(task: any): boolean {
  const state = task?.taskState;
  if (!state) return false;
  return state !== "DELIVERED";
}

// Same dedupe rule as collectPackageMatches but collects every expected
// return (no search query): one entry per scannableId, preferring the task
// at the non-station stop (sequenceNumber !== 1).
// Rows come from either itinerarySummaries or the routeSummaries fallback,
// each carrying its own routeCode/serviceAreaId so multi-route drivers keep
// the correct attribution per task.
export function collectExpectedReturns(
  rows: { stops: any; routeCode?: string | null; serviceAreaId?: string | null }[],
): Map<
  string,
  {
    task: any;
    stop: any;
    stopNumber: number | null;
    routeCode: string | null;
    serviceAreaId: string | null;
  }
> {
  const map = new Map<
    string,
    {
      task: any;
      stop: any;
      stopNumber: number | null;
      routeCode: string | null;
      serviceAreaId: string | null;
    }
  >();

  // Pre-pass: scannableIds that have a delivery/return leg anywhere in the
  // data. For those packages the leg's task state decides "is it coming
  // back" — the PICKED_UP load scan is just the manifest entry, wherever
  // Amazon sequenced the pickup stop (it is not always sequenceNumber 1).
  const hasDeliveryLeg = new Set<string>();
  for (const row of rows) {
    const stops = Array.isArray(row.stops) ? row.stops : [];
    for (const stop of stops) {
      for (const task of Array.isArray(stop?.tasks) ? stop.tasks : []) {
        if (task?.taskType && task.taskType.toLowerCase() !== "pick_up") {
          const id = getScannableId(task);
          if (typeof id === "string") hasDeliveryLeg.add(id.toUpperCase());
        }
      }
    }
  }

  for (const row of rows) {
    const rowRouteCode = row.routeCode ?? null;
    const rowServiceAreaId = row.serviceAreaId ?? null;
    const stops = Array.isArray(row.stops) ? row.stops : [];
    for (const stop of stops) {
      const stopNumber = stop?.sequenceNumber ?? null;
      for (const task of Array.isArray(stop?.tasks) ? stop.tasks : []) {
        const scannableId = getScannableId(task);
        if (!scannableId || typeof scannableId !== "string") continue;
        const upperId = scannableId.toUpperCase();
        // A PICKED_UP scan is a load scan, not a return — skip it when it is
        // the truncated station leftover (sequenceNumber 1) or when a
        // delivery/return leg exists to carry the verdict. Pickup-only
        // packages keep their pickup task so they are not invisible.
        const isLoadScan =
          task?.taskType?.toLowerCase() === "pick_up" &&
          task?.taskState?.toLowerCase() === "picked_up";
        if (isLoadScan && (stopNumber === 1 || hasDeliveryLeg.has(upperId))) continue;
        if (!isExpectedReturnTask(task)) continue;
        const existing = map.get(upperId);
        if (existing && stopNumber === 1) continue;
        if (existing && existing.stopNumber !== 1) continue;
        map.set(upperId, {
          task,
          stop,
          stopNumber,
          routeCode: rowRouteCode,
          serviceAreaId: rowServiceAreaId,
        });
      }
    }
  }
  return map;
}
// Maps an RTS return-reason code (e.g. "BUSINESS_CLOSED") to its display label
// ("Business Closed"). Unknown codes pass through as-is; null/undefined -> "".
export const reasonLabel = (code: string | null | undefined): string =>
  code ? (RTS_RETURN_REASONS.find((r) => r.code === code)?.label ?? code) : "";

// Removes HTML tags and collapses whitespace. Needed client-side because the
// ATS chat input is plain text — an email template's htmlBody must become
// readable text before it can be dropped into a chat message. (Moved here
// from atsNotificationService.ts, which can't be imported by client code
// since it pulls in db/SES/Firebase.)
export function stripHtml(html: string): string {
  return html
    // Turn block-level boundaries into newlines BEFORE removing tags —
    // otherwise paragraphs and list items collapse into one run-on line in
    // the chat input and SMS bodies. Opening tags are boundaries too so a
    // block mid-flow (e.g. text<ul><li>) doesn't fuse to the text before it.
    .replace(/<br\b[^>]*>/gi, "\n")
    .replace(/<\/?(p|div|ul|ol|h[1-6]|tr|table|blockquote)\b[^>]*>/gi, "\n")
    // "- " goes on the OPENING li with its own newline — </li> may legally
    // be omitted in HTML, so relying on it for the line break would fuse
    // unclosed items ("- a- b") back together.
    .replace(/<li\b[^>]*>/gi, "\n- ")
    .replace(/<[^>]*>/g, "") // remove remaining tags
    // Decode common entities — &amp; must be last: decoding it first would
    // turn "&amp;lt;" into "&lt;" and then wrongly emit a literal "<".
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    // Collapse horizontal whitespace only — the newlines created above must
    // survive. [^\S\n] matches spaces/tabs but not line breaks.
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n") // trim spaces hugging newlines
    .replace(/\n{3,}/g, "\n\n") // cap consecutive blank lines
    .trim();
}

// Replaces {{key}} placeholders for keys present in `vars`, but leaves
// unknown {{vars}} untouched (unlike the send-side mergeTemplate, which
// blanks them). Used for the chat template preview/insert: the client only
// knows a few vars (name, email, position) — blanking placeholders only the
// server can resolve (portalUrl, orgName, slot*) would destroy them before
// the send route could fill them authoritatively.
export function resolveTemplateVars(
  template: string,
  vars: Record<string, string | undefined>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    vars[key] !== undefined ? vars[key]! : match,
  );
}
